import { Injectable, signal } from '@angular/core';

/** Lo que se le pregunta al usuario antes de una acción que no se puede deshacer. */
export interface ConfirmRequest {
  /** Título corto, la acción en infinitivo: "Desactivar casilla", "Eliminar aplicación". */
  title: string;
  /** La pregunta y su consecuencia inmediata. */
  message: string;
  /** Advertencia destacada: lo que se pierde, lo que queda registrado, a quién afecta. */
  detail?: string;
  /** Texto del botón que confirma. Por defecto "Confirmar". */
  confirmLabel?: string;
  /** Texto del botón que cancela. Por defecto "Cancelar". */
  cancelLabel?: string;
  /** Pinta el botón de confirmación en rojo: la acción borra o corta algo. */
  danger?: boolean;
}

/** La pregunta abierta, ya con los textos por defecto resueltos. */
export type PendingConfirm = ConfirmRequest &
  Required<Pick<ConfirmRequest, 'confirmLabel' | 'cancelLabel' | 'danger'>>;

/**
 * Confirmaciones de la app. Existe para no usar el `confirm()` del navegador, que anuncia
 * "localhost:4200 dice", ignora el tema del panel y muestra igual un cambio de estado
 * reversible y una baja definitiva.
 *
 * `ask()` devuelve una promesa con la respuesta, así el llamador conserva la forma que ya
 * tenía (`if (!ok) return;`). El diálogo lo pinta `ConfirmDialog`, montado una sola vez en
 * la raíz de la app.
 */
@Injectable({ providedIn: 'root' })
export class ConfirmService {
  readonly pending = signal<PendingConfirm | null>(null);
  private resolver: ((ok: boolean) => void) | null = null;

  ask(request: ConfirmRequest): Promise<boolean> {
    // Solo puede haber una pregunta a la vez: si quedara otra abierta su promesa nunca se
    // resolvería, así que se la cancela.
    this.resolver?.(false);
    this.pending.set({
      ...request,
      confirmLabel: request.confirmLabel ?? 'Confirmar',
      cancelLabel: request.cancelLabel ?? 'Cancelar',
      danger: request.danger ?? false,
    });
    return new Promise<boolean>((resolve) => {
      this.resolver = resolve;
    });
  }

  resolve(ok: boolean) {
    const resolver = this.resolver;
    this.resolver = null;
    this.pending.set(null);
    resolver?.(ok);
  }
}
